import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    register: (data) => api.post('/auth/register', data),
    login: (data) => api.post('/auth/login', data),
    getMe: () => api.get('/auth/me'),
    submitContactEnquiry: (data) => api.post('/contact/enquiry', data),
    listContactEnquiries: () => api.get('/contact/enquiries'),
    requestPasswordReset: (email) => api.post('/auth/password-reset/request', { email }),
    confirmPasswordReset: (token, newPassword) => api.post('/auth/password-reset/confirm', { token, new_password: newPassword }),
    refreshToken: () => api.post('/auth/refresh'),
};

// Tenant API
export const tenantAPI = {
    create: (data) => api.post('/tenants/', data),
    getAll: () => api.get('/tenants/'),
    getOne: (id) => api.get(`/tenants/${id}`),
    update: (id, data) => api.put(`/tenants/${id}`, data),
    updateAccountingProfile: (id, data) => api.put(`/tenants/${id}/accounting-profile`, data),
    // Document counts for EVERY visible client in one aggregate call (companies list).
    getDocumentCounts: () => api.get('/tenants/document-counts'),
    // Per-client inbound "forward-to-inbox" address (Resend). rotate=true retires the old code.
    generateInboundCode: (id, rotate = false) => api.post(`/tenants/${id}/inbound-code${rotate ? '?rotate=true' : ''}`),
    // One immediate inbox poll for this client, through the same path as the scheduled poll.
    // 429 when called again inside the cooldown; 409 if ingest is off or the client is protected.
    inboxPollNow: (id) => api.post(`/tenants/${id}/inbox/poll-now`),
    // Uniconta connection (scaffold): status preview + manual-fallback downloads (finanskladde CSV + bilag ZIP).
    unicontaPreview: (id) => api.get(`/push/uniconta/${id}/preview`),
    unicontaCsv: (id) => api.get(`/push/uniconta/${id}/csv`, { responseType: 'blob' }),
    unicontaBilag: (id) => api.get(`/push/uniconta/${id}/bilag`, { responseType: 'blob' }),
    unicontaTestConnection: (id) => api.post(`/push/uniconta/${id}/test-connection`),
    // dryRun=true returns the payload that WOULD post, without sending anything.
    unicontaPush: (id, dryRun = false) =>
        api.post(`/push/uniconta/${id}/push`, null, { params: { dry_run: dryRun } }),
    // DESTRUCTIVE — wipe one client's working/test data (config kept). Requires the client name echoed.
    resetTestData: (id, confirmName, clearLearnedPatterns = false) =>
        api.post(`/tenants/${id}/reset-test-data`, { confirm_name: confirmName, clear_learned_patterns: clearLearnedPatterns }),
    addUser: (tenantId, email) => api.post(`/tenants/${tenantId}/users/${email}`),
    assignEndUser: (tenantId, email) => api.post(`/tenants/${tenantId}/assign-end-user`, { email }),
    // Provider Configuration
    getProviderConfig: (tenantId) => api.get(`/tenants/${tenantId}/provider`),
    updateProviderConfig: (tenantId, config) => api.put(`/tenants/${tenantId}/provider`, config),
    testProviderConnection: (tenantId) => api.post(`/tenants/${tenantId}/provider/test`),
};

// Document API
export const documentAPI = {
    upload: (file, tenantId, docCategory = 'invoice', documentType = null) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('tenant_id', tenantId);
        formData.append('doc_category', docCategory);
        if (documentType) formData.append('document_type', documentType);
        return api.post('/documents/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    getAll: (tenantId = null, status = null, category = null) => {
        const params = new URLSearchParams();
        if (tenantId) params.append('tenant_id', tenantId);
        if (status) params.append('status', status);
        if (category) params.append('category', category);
        return api.get(`/documents/?${params.toString()}`);
    },
    getOne: (id) => api.get(`/documents/${id}`),
    // Durable processing queue (Part B): live progress for the tenant + re-queue a failed doc.
    queueStatus: (tenantId) => api.get('/documents/queue-status', { params: { tenant_id: tenantId } }),
    reprocess: (id) => api.post(`/documents/${id}/reprocess`),
    getFile: (id, download = false) => api.get(`/documents/${id}/file`, {
        responseType: 'blob',
        params: download ? { download: true } : {},
    }),
    deleteDocument: (id) => api.delete(`/documents/${id}`),
    editFields: (id, fieldUpdates, confirmApprovedEdit = false) =>
        api.put(`/documents/${id}/edit`, { field_updates: fieldUpdates, confirm_approved_edit: confirmApprovedEdit }),
    approve: (id, approved, finalData = null, accountMapping = null) =>
        api.put(`/documents/${id}/approve`, { approved, final_data: finalData, account_mapping: accountMapping }),
};

// Document line review (Phase 1: line-by-line adjustment surface)
export const documentLinesAPI = {
    // Single-screen review support
    reviewQueue: (tenantId) => api.get(`/documents/review-queue/${tenantId}`),
    bankMatch: (docId) => api.get(`/documents/${docId}/bank-match`),
    clearDuplicateFlag: (docId) => api.post(`/documents/${docId}/not-duplicate`),
    clientChart: (tenantId, refresh = false) =>
        api.get(`/accounting-data/client-chart/${tenantId}`, { params: { refresh } }),
    getLines: (docId) => api.get(`/documents/${docId}/lines`),
    saveLines: (docId, lines, confirmApprovedEdit = false) =>
        api.put(`/documents/${docId}/lines`, { lines, confirm_approved_edit: confirmApprovedEdit }),
    approveLine: (docId, lineId, reviewFlow = 'multi_step') =>
        api.post(`/documents/${docId}/lines/${lineId}/approve`, null, { params: { review_flow: reviewFlow } }),
    unapproveLine: (docId, lineId) => api.post(`/documents/${docId}/lines/${lineId}/unapprove`),
    approveDocument: (docId, reviewFlow = 'multi_step') =>
        api.post(`/documents/${docId}/approve-lines`, null, { params: { review_flow: reviewFlow } }),
    setDocumentType: (docId, documentType) => api.put(`/documents/${docId}/document-type`, { document_type: documentType }),
};

// Accounting data API (real chart of accounts + VAT codes for the pickers)
export const accountingAPI = {
    getRealChart: () => api.get('/accounting-data/real-chart-of-accounts'),
    getVatCodes: () => api.get('/accounting-data/vat-codes'),
    getClassification: () => api.get('/accounting-data/classification'),
};

// Voucher API
export const voucherAPI = {
    getAll: (tenantId, status = null) => {
        const params = status ? `?status=${status}` : '';
        return api.get(`/vouchers/${tenantId}${params}`);
    },
    getOne: (tenantId, voucherId) => api.get(`/vouchers/${tenantId}/${voucherId}`),
    // push retired (Step 1a) — e-conomic delivery is on the Bank export page (CSV/inbox or direct push).
    delete: (tenantId, voucherId) => api.delete(`/vouchers/${tenantId}/${voucherId}`),
};

// Notifications API (accountant/staff in-app notifications)
export const notificationsAPI = {
    list: (limit = 50) => api.get(`/notifications?limit=${limit}`),
    markRead: (id) => api.post(`/notifications/${id}/read`),
    markAllRead: () => api.post('/notifications/read-all'),
};

// Staff API (firm owner manages their firm's staff)
export const staffAPI = {
    list: () => api.get('/staff'),
    add: (data) => api.post('/staff', data),
    setAccess: (staffId, access) => api.put(`/staff/${staffId}/access`, { access }),
    remove: (staffId) => api.delete(`/staff/${staffId}`),
};

// Vendor API
export const vendorAPI = {
    getPatterns: (tenantId) => api.get(`/vendors/${tenantId}`),
    updatePattern: (tenantId, patternId, updates) => api.put(`/vendors/${tenantId}/${patternId}`, updates),
};

// Activity API
export const activityAPI = {
    getLogs: (tenantId, activityType = null, limit = 100) => {
        const params = new URLSearchParams();
        if (activityType) params.append('activity_type', activityType);
        params.append('limit', limit);
        return api.get(`/activity/${tenantId}?${params.toString()}`);
    },
    getTimeSaved: (tenantId, days = 30) => api.get(`/activity/${tenantId}/time-saved?days=${days}`),
};

// Reconciliation API
export const reconciliationAPI = {
    getUnmatched: (tenantId) => api.get(`/reconciliation/${tenantId}/unmatched`),
    match: (tenantId, data) => api.post(`/reconciliation/${tenantId}/match`, data),
    bulkApprove: (tenantId, matches) => api.post(`/reconciliation/${tenantId}/bulk-approve`, matches),
    importStatement: (tenantId, file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post(`/reconciliation/${tenantId}/import`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    // Import an already-delivered bank-statement document (doc_category='bank_statement')
    // into reconciliation — accountant-triggered, never automatic.
    importDocument: (tenantId, docId) => api.post(`/reconciliation/${tenantId}/import-document/${docId}`),
    book: (tenantId, payload) => api.post(`/reconciliation/${tenantId}/book`, payload),
    ignore: (tenantId, transactionId) => api.post(`/reconciliation/${tenantId}/ignore`, { transaction_id: transactionId }),
    // Approved invoices with no matching bank line — { open, paid_elsewhere }.
    getOpenInvoices: (tenantId) => api.get(`/reconciliation/${tenantId}/open-invoices`),
    // Document an invoice as paid outside this bank (local annotation, no e-conomic write).
    markPaidElsewhere: (tenantId, invoiceId, reason, note) =>
        api.post(`/reconciliation/${tenantId}/invoice/${invoiceId}/paid-elsewhere`, { reason, note }),
    reopenInvoice: (tenantId, invoiceId) =>
        api.post(`/reconciliation/${tenantId}/invoice/${invoiceId}/reopen`),
    // Slice-3 learning: inspect / forget this client's learned bank patterns (accounts + aliases).
    getLearned: (tenantId) => api.get(`/reconciliation/${tenantId}/learned`),
    forgetLearned: (tenantId, patternKey) =>
        api.delete(`/reconciliation/${tenantId}/learned`, { params: { pattern_key: patternKey } }),
};

// VAT API
export const vatAPI = {
    getAnalysis: (tenantId, periodStart = null, periodEnd = null) => {
        const params = new URLSearchParams();
        if (periodStart) params.append('period_start', periodStart);
        if (periodEnd) params.append('period_end', periodEnd);
        return api.get(`/vat/${tenantId}/analysis?${params.toString()}`);
    },
    generateReport: (tenantId, periodStart, periodEnd) => 
        api.get(`/vat/${tenantId}/report?period_start=${periodStart}&period_end=${periodEnd}`),
};

// Billing API
// There is no plan list any more — the price is one number from GET /api/pricing and
// what a firm pays comes back on /billing/subscription as `billing`.
// The three Portfolio screens used raw fetch() with a hand-built Authorization header, so they
// bypassed this module's 401 interceptor entirely: an expired token failed silently there instead
// of clearing the session and sending the user to /login. They now go through `api` like
// everything else.
export const portfolioAPI = {
    summary: () => api.get('/portfolio/summary'),
    clients: (sortBy = 'risk_score', sortOrder = 'desc') =>
        api.get(`/portfolio/clients?sort_by=${sortBy}&sort_order=${sortOrder}`),
    client: (clientId) => api.get(`/portfolio/clients/${clientId}`),
    exceptions: (params = '') => api.get(`/portfolio/exceptions${params ? `?${params}` : ''}`),
    actOnException: (exceptionId, body) =>
        api.post(`/portfolio/exceptions/${exceptionId}/action`, body),
    preVatReview: (days = 14) => api.get(`/portfolio/pre-vat-review?days=${days}`),
};

export const pricingAPI = {
    get: () => api.get('/pricing'),
};

export const billingAPI = {
    requestSubscription: (planId) => api.post(`/billing/request?plan_id=${planId}`),
    getCurrentSubscription: () => api.get('/billing/subscription'),
    cancelSubscription: () => api.delete('/billing/subscription'),
};

// Dashboard API
// Morning overview — replaces dashboardAPI.getStats() as the accountant's landing screen.
export const overviewAPI = {
    get: () => api.get('/dashboard/overview'),
    acknowledge: (body) => api.post('/dashboard/attention/acknowledge', body),
    unacknowledge: (kind, scopeKey) =>
        api.delete(`/dashboard/attention/acknowledge?kind=${encodeURIComponent(kind)}&scope_key=${encodeURIComponent(scopeKey)}`),
};

export const dashboardAPI = {
    getStats: () => api.get('/dashboard/stats'),
    getAccountantOverview: () => api.get('/accountant/overview'),
};

// Admin API
export const adminAPI = {
    getUsers: () => api.get('/admin/users'),
    getStats: () => api.get('/admin/stats'),
    updateUserRole: (userId, role) => api.put(`/admin/users/${userId}/role?role=${role}`),
    getSubscriptionRequests: () => api.get('/admin/subscription-requests'),
    activateSubscription: (userId, planId, notes = null) =>
        api.post('/admin/subscriptions/activate', { user_id: userId, plan_id: planId, notes }),
    getPlatformStats: () => api.get('/admin/platform-stats'),
    deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
    // Platform-admin dashboard + activity/audit (admin only, read).
    dashboard: () => api.get('/admin/dashboard'),
    activity: (params = {}) => api.get('/admin/activity', { params }),
    usageByFirm: (period = null) => api.get('/admin/usage/by-firm', { params: period ? { period } : {} }),
    // Firm hierarchy + per-firm billing calculation (platform admin only).
    listFirms: () => api.get('/admin/firms'),
    getFirm: (firmId) => api.get(`/admin/firms/${firmId}`),
    setFirmPrice: (firmId, pricePerClient) =>
        api.put(`/admin/firms/${firmId}/price`, { price_per_client: pricePerClient }),
    updateFirm: (firmId, data) => api.put(`/admin/firms/${firmId}`, data),
    setClientStatus: (tenantId, status) => api.put(`/admin/clients/${tenantId}/status`, { status }),
    firmBilling: (period = null) => api.get('/admin/billing/firms', { params: period ? { period } : {} }),
    downloadFirmBillingCsv: (period = null) =>
        api.get('/admin/billing/firms.csv', { params: period ? { period } : {}, responseType: 'blob' }),
    // Volume-test harness (Part B rehearsal — test data only, admin).
    volumeTestEnqueue: (count, tenants = 2) => api.post('/admin/test/volume/enqueue', { count: Number(count), tenants }),
    volumeTestStatus: () => api.get('/admin/test/volume/status'),
    volumeTestCleanup: () => api.post('/admin/test/volume/cleanup'),
    // GDPR full erasure (admin; destructive, irreversible; name-echo confirmed).
    gdprEraseTenant: (tenantId, confirmName) => api.post(`/admin/gdpr/tenant/${tenantId}/erase`, { confirm_name: confirmName }),
    gdprEraseFirm: (firmId, confirmName) => api.post(`/admin/gdpr/firm/${firmId}/erase`, { confirm_name: confirmName }),
};

// Feedback API (Beta)
export const feedbackAPI = {
    submit: (data) => api.post('/feedback', data),
    getAll: () => api.get('/feedback'),
};

// Export API (Beta)
export const exportAPI = {
    exportVouchers: (tenantId, options = {}) =>
        api.post(`/export/${tenantId}/vouchers`, options, {
            responseType: options.format === 'csv' ? 'text' : 'json'
        }),
    // Phase 2a — e-conomic kassekladde CSV export
    kassekladdePending: (tenantId) => api.get(`/export/${tenantId}/kassekladde/pending`),
    // documentIds=null => batch (all approved, not-yet-exported). Returns a CSV blob.
    downloadKassekladde: (tenantId, documentIds = null) =>
        api.post(`/export/${tenantId}/kassekladde`, { document_ids: documentIds }, { responseType: 'blob' }),
    unexportDocument: (tenantId, docId) => api.post(`/export/${tenantId}/kassekladde/${docId}/unexport`),
};

// Uniconta onboarding at OWNER level. One server-user login covers every company the accountant
// manages, so it is entered once here rather than on each company card. Company cards resolve the
// credentials at call time; a company that already carries its own keeps using those.
// Mirrors history_import.DEFAULT_MONTHS. Two years, because a one-year window was what held
// the result down, not the dominance threshold.
export const HISTORY_IMPORT_MONTHS = 24;

export const historyImportAPI = {
    // Preview is a POST because it performs a live read of the client's books; it writes nothing.
    preview: (tenantId, months = HISTORY_IMPORT_MONTHS) =>
        api.post(`/history-import/${tenantId}/preview?months=${months}`),
    apply: (tenantId, vendorIdentifiers, months = HISTORY_IMPORT_MONTHS, confirmedLinks = {}) =>
        api.post(`/history-import/${tenantId}/apply`,
            { vendor_identifiers: vendorIdentifiers, months, confirmed_links: confirmedLinks }),
};

export const unicontaOwnerAPI = {
    status: () => api.get('/uniconta/owner/status'),
    saveCredentials: (username, password) =>
        api.post('/uniconta/owner/credentials', { username, password }),
    deleteCredentials: () => api.delete('/uniconta/owner/credentials'),
    companies: () => api.get('/uniconta/owner/companies'),
    journals: (companyId) => api.get(`/uniconta/owner/companies/${companyId}/journals`),
    selectCompanies: (companies) => api.post('/uniconta/owner/companies/select', { companies }),
};

// Bank-driven engine — Phase 1: bank-statement ingest (upload + mapping + stored lines).
export const bankAPI = {
    getMapping: (tenantId) => api.get(`/bank/${tenantId}/mapping`),
    preview: (tenantId, file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post(`/bank/${tenantId}/preview`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    importStatement: (tenantId, file, mapping, fiscalYear = null) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('mapping', JSON.stringify(mapping));
        if (fiscalYear != null && fiscalYear !== '') formData.append('fiscal_year', fiscalYear);
        return api.post(`/bank/${tenantId}/import`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
    },
    getLines: (tenantId, fiscalYear = null) =>
        api.get(`/bank/${tenantId}/lines`, { params: fiscalYear != null ? { fiscal_year: fiscalYear } : {} }),
    clearLines: (tenantId, fiscalYear = null) =>
        api.delete(`/bank/${tenantId}/lines`, { params: fiscalYear != null ? { fiscal_year: fiscalYear } : {} }),
    // Phase 2 — matching: three buckets + confirm / reject / relink (relink = confirm with a receipt_id).
    getMatches: (tenantId, fiscalYear) =>
        api.get(`/bank/${tenantId}/match`, { params: { fiscal_year: fiscalYear } }),
    confirmMatch: (tenantId, bankLineId, receiptId = null) =>
        api.post(`/bank/${tenantId}/match/confirm`, { bank_line_id: bankLineId, receipt_id: receiptId }),
    rejectMatch: (tenantId, bankLineId) =>
        api.post(`/bank/${tenantId}/match/reject`, { bank_line_id: bankLineId }),
    // Phase 3 — coding: proposed account + VAT per matched/bank-only line, accept / correct.
    getCoding: (tenantId, fiscalYear) =>
        api.get(`/bank/${tenantId}/coding`, { params: { fiscal_year: fiscalYear } }),
    acceptCoding: (tenantId, bankLineId) =>
        api.post(`/bank/${tenantId}/coding/accept`, { bank_line_id: bankLineId }),
    correctCoding: (tenantId, bankLineId, { accountCode, accountName, vatCode, kind } = {}) =>
        api.post(`/bank/${tenantId}/coding/correct`, {
            bank_line_id: bankLineId, account_code: accountCode, account_name: accountName,
            vat_code: vatCode, kind,
        }),
    // Phase 4 — contra: confirm a privately-paid (receipt-only) receipt -> mellemregning.
    confirmPrivate: (tenantId, receiptId, confirmed = true) =>
        api.post(`/bank/${tenantId}/coding/confirm-private`, { receipt_id: receiptId, confirmed }),
    // Phase 5 — export: materialized kassekladde rows + needs-attention, and the e-conomic CSV download.
    getExport: (tenantId, fiscalYear) =>
        api.get(`/bank/${tenantId}/export`, { params: { fiscal_year: fiscalYear } }),
    downloadKassekladde: (tenantId, fiscalYear) =>
        api.get(`/bank/${tenantId}/export/csv`, { params: { fiscal_year: fiscalYear }, responseType: 'blob' }),
    // e-conomic Plus push (separate /api/push/plus path; api baseURL already includes /api).
    // preview = read-only rows + push status; push = DRAFT vouchers + bilag (optional bilag subset).
    pushPlusPreview: (tenantId, fiscalYear) =>
        api.get(`/push/plus/${tenantId}/preview`, { params: { fiscal_year: fiscalYear } }),
    pushPlus: (tenantId, fiscalYear, bilag = null) =>
        api.post(`/push/plus/${tenantId}/push`, { fiscal_year: Number(fiscalYear), bilag }),
    // Basic-tier (economic_basis) bilag delivery: emails numbered bilag to the client's e-conomic
    // scan inbox. preview = per-bilag coverage; deliver = send one email per bilag (idempotent).
    deliverInboxPreview: (tenantId, fiscalYear) =>
        api.get(`/bank/${tenantId}/deliver-inbox/preview`, { params: { fiscal_year: fiscalYear } }),
    deliverInbox: (tenantId, fiscalYear, { bilag = null, force = false } = {}) =>
        api.post(`/bank/${tenantId}/deliver-inbox`, { fiscal_year: Number(fiscalYear), bilag, force }),
    // Recover a bilag without depending on e-conomic: download one original (numbered filename),
    // or bundle every bilag as a zip. Re-deliver-one reuses deliverInbox with { bilag:[n], force:true }.
    downloadBilag: (tenantId, fiscalYear, bilag) =>
        api.get(`/bank/${tenantId}/bilag/${bilag}/file`, { params: { fiscal_year: fiscalYear }, responseType: 'blob' }),
    downloadBilagZip: (tenantId, fiscalYear) =>
        api.get(`/bank/${tenantId}/bilag.zip`, { params: { fiscal_year: fiscalYear }, responseType: 'blob' }),
};

// Trigger a browser download from an axios blob response. Uses the response's own content-type so
// PDFs/zips download with the right MIME (falls back to text/csv for the CSV export).
export const downloadBlob = (response, fallbackName = 'export.csv') => {
    const cd = response.headers?.['content-disposition'] || '';
    const m = /filename="?([^"]+)"?/.exec(cd);
    const name = m ? m[1] : fallbackName;
    const type = response.headers?.['content-type'] || 'text/csv';
    const url = window.URL.createObjectURL(new Blob([response.data], { type }));
    const a = document.createElement('a');
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
};

export default api;
